/**
 * Paiement des packs de gemmes — le côté navigateur (0.38.0, mode test Stripe).
 *
 * Deux appels, deux fonctions Edge :
 *
 * - `stripe-checkout` crée la session et rend l'URL de paiement — le CLIENT n'envoie
 *   qu'un identifiant de pack, jamais un prix : le catalogue qui encaisse vit côté
 *   serveur ;
 * - `stripe-verify` dit si une session est PAYÉE et combien de gemmes elle vaut —
 *   relu par le serveur chez Stripe, jamais cru sur l'URL de retour.
 *
 * ══ Idempotence du crédit — DÉPLACÉE DANS LA BASE (ROADMAP 0.1) ══════════════
 *
 * Elle vivait ici, dans une liste de sessions déjà encaissées rangée en
 * `localStorage`. Ce garde-fou avait le défaut exact du problème qu'il gardait :
 * vider le stockage local le faisait disparaître, en même temps que les gemmes.
 *
 * Elle vit maintenant dans l'unicité `(source, reference)` de `gem_grants`, et le
 * crédit ne se calcule plus session par session mais par RÉCONCILIATION — le total
 * octroyé par le serveur, moins ce que le snapshot a déjà absorbé
 * (`core/paidGems.ts`). Deux conséquences qu'on cherchait :
 *
 * - rejouer une session dix fois ne crédite rien de plus, sans aucune liste locale ;
 * - un `localStorage` vidé récupère TOUT ce qui a été payé, ce que la liste locale
 *   rendait au contraire impossible.
 *
 * Ces fonctions ne rendent donc plus « combien créditer » mais **le total connu du
 * serveur**. C'est l'appelant qui réconcilie, en un seul endroit.
 */

import { ensureAuthSession, getSupabase, isSupabaseConfigured } from './supabase';

/**
 * Sessions LANCÉES et pas encore réglées dans le jeu.
 *
 * ══ Pourquoi cette liste existe (0.49.0, passage en paiement réel) ═══════════
 *
 * Le crédit ne se faisait qu'au RETOUR de Stripe, à la lecture de `session_id`
 * dans l'URL. Tout ce qui casse ce retour — onglet fermé après le paiement,
 * réseau coupé pendant la redirection, navigateur qui bloque, joueur qui revient
 * par un favori — encaissait l'argent sans livrer les gemmes. En mode test,
 * c'était une gêne ; avec de l'argent réel, c'est le pire défaut possible.
 *
 * La session est donc mémorisée AVANT de partir, et rejouée à chaque démarrage
 * jusqu'à ce qu'elle soit payée. Le joueur peut revenir cinq minutes ou deux
 * jours plus tard, par n'importe quel chemin : ses gemmes l'attendent.
 */
const PENDING_KEY = 'villopia:checkout:pending';

/** Au-delà, une session non payée est abandonnée : Stripe les expire sous 24 h. */
const PENDING_TTL_MS = 72 * 60 * 60 * 1000;

interface PendingSession {
  readonly id: string;
  readonly at: number;
}

function readPending(): PendingSession[] {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    const parsed: unknown = raw === null ? [] : JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is PendingSession =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof (entry as PendingSession).id === 'string' &&
        typeof (entry as PendingSession).at === 'number',
    );
  } catch {
    return [];
  }
}

function writePending(list: readonly PendingSession[]): void {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(list.slice(-20)));
  } catch {
    // Stockage indisponible : la récupération disparaît, le crédit direct reste.
  }
}

function rememberPending(sessionId: string): void {
  const list = readPending().filter((entry) => entry.id !== sessionId);
  writePending([...list, { id: sessionId, at: Date.now() }]);
}

function forgetPending(sessionId: string): void {
  writePending(readPending().filter((entry) => entry.id !== sessionId));
}

export type CheckoutStart = 'redirected' | 'unavailable' | 'error';

/** Lance le paiement d'un pack : redirige vers Stripe, ou explique pourquoi pas. */
export async function startCheckout(packId: string): Promise<CheckoutStart> {
  if (!isSupabaseConfigured()) return 'unavailable';

  // ⚠️ L'identité D'ABORD. `stripe-checkout` estampille la session au nom du payeur
  // et refuse un appel anonyme : on ne prend pas d'argent qu'on ne saurait pas
  // attribuer. Sans identité, la boutique le dit plutôt que d'échouer chez Stripe.
  if ((await ensureAuthSession()) === null) return 'unavailable';

  try {
    const response: { data: unknown; error: unknown } = await getSupabase().functions.invoke(
      'stripe-checkout',
      { body: { packId, origin: location.origin } },
    );

    if (response.error !== null) return 'error';

    const body = response.data as { url?: unknown; available?: unknown; sessionId?: unknown };
    // « Pas encore ouvert » arrive en 200 : une offre fermée est une réponse, pas une
    // panne — et un statut d'erreur salirait la console de tous les joueurs.
    if (body.available === false) return 'unavailable';
    if (typeof body.url !== 'string') return 'error';

    // ⚠️ On mémorise AVANT de partir : après `location.assign`, plus une ligne de ce
    // code ne tourne. C'est ce qui rend un achat payé récupérable même si le retour
    // n'a jamais lieu.
    if (typeof body.sessionId === 'string' && body.sessionId !== '') {
      rememberPending(body.sessionId);
    }

    location.assign(body.url);
    return 'redirected';
  } catch {
    return 'error';
  }
}

export interface CheckoutResult {
  /** Ce que valait CETTE session. Sert au libellé, jamais au crédit. */
  readonly gems: number;
  /**
   * Total octroyé au joueur, registre serveur compris. C'est LUI qui pilote le
   * crédit, via `reconcilePaidGems`. `null` si le registre n'a pas répondu.
   */
  readonly total: number | null;
}

/**
 * Vérifie une session au retour de Stripe. Rend `null` si elle n'est pas payée,
 * inconnue, ou attribuée à quelqu'un d'autre.
 *
 * Rejouer la même session est SANS EFFET de bord : l'octroi est déjà au registre,
 * le total revient identique, et la réconciliation ne crédite rien.
 */
export async function verifyCheckout(sessionId: string): Promise<CheckoutResult | null> {
  if (!isSupabaseConfigured()) return null;
  if ((await ensureAuthSession()) === null) return null;

  try {
    const response: { data: unknown; error: unknown } = await getSupabase().functions.invoke(
      'stripe-verify',
      { body: { sessionId } },
    );
    if (response.error !== null) return null;

    const body = response.data as {
      paid?: unknown;
      gems?: unknown;
      final?: unknown;
      total?: unknown;
    };
    if (body.paid !== true || typeof body.gems !== 'number' || body.gems <= 0) {
      // Session inconnue, expirée, ou appartenant à un autre joueur : elle ne sera
      // JAMAIS payée POUR CELUI-CI, on cesse de la rejouer à chaque démarrage. Une
      // session simplement en attente reste, elle.
      if (body.final === true) forgetPending(sessionId);
      return null;
    }

    forgetPending(sessionId);
    const total = typeof body.total === 'number' && body.total >= 0 ? body.total : null;
    return { gems: body.gems, total };
  } catch {
    return null;
  }
}

/**
 * Rejoue les paiements lancés dont le retour n'a jamais abouti.
 *
 * Appelée au démarrage : le joueur qui a payé hier et fermé son onglet trouve ses
 * gemmes en rouvrant le jeu. Rend le TOTAL du registre après rejeu, `null` s'il n'y
 * avait rien à rejouer ou si le serveur n'a rien dit.
 *
 * Le total et non la somme récupérée : deux sessions rejouées d'affilée rendent
 * chacune le même total cumulé, et les additionner créditerait deux fois. C'est la
 * réconciliation qui tranche, une seule fois, sur la dernière valeur connue.
 *
 * Une session qui reste impayée au-delà de trois jours est ABANDONNÉE : Stripe
 * expire les siennes sous 24 h, et une liste qui ne se vide jamais finirait par
 * interroger le serveur à chaque démarrage pour rien.
 */
export async function recoverPendingCheckouts(): Promise<number | null> {
  if (!isSupabaseConfigured()) return null;

  const now = Date.now();
  const pending = readPending();
  if (pending.length === 0) return null;

  let total: number | null = null;
  for (const entry of pending) {
    if (now - entry.at > PENDING_TTL_MS) {
      forgetPending(entry.id);
      continue;
    }
    const result = await verifyCheckout(entry.id);
    if (result !== null && result.total !== null) total = result.total;
  }
  return total;
}
